import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Modal,
    IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import api from '../../api';
import { toast } from 'sonner';
import { clearAllCookies } from '../../pages/utils/clearCookie';

const AccountDeletion = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const userData = JSON.parse(localStorage.getItem("User"));
    const token = JSON.parse(localStorage.getItem("token"))?.token;

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#fff',
            '& fieldset': {
                borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
                borderColor: '#bdbdbd',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
            },
        },
        '& .MuiInputBase-input': {
            padding: '10px 14px',
        },
    };

    const handleOpenModal = () => {
        if (email !== userData.Email) {
            return toast.error("Please enter your email to delete your profile");
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleDeleteAccount = async () => {
        setLoading(true);
        try {
            const response = await api.delete(`/users/delete-profile`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Clear local storage, session storage, and cookies with organization data backup
            try {
                // Attempt to clear cookies first
                clearAllCookies();
                
                // Verify cookies were successfully cleared
                const cookiesStatus = document.cookie.split(';')
                    .filter(cookie => cookie.trim() !== '')
                    .filter(cookie => !cookie.trim().startsWith('_ga') && !cookie.trim().startsWith('_gid'));
                
                if (cookiesStatus.length === 0) {
                    // Cookies cleared successfully, now clear storage
                    localStorage.clear();
                    sessionStorage.clear();
                } else {
                    console.error("Failed to clear all cookies, aborting storage clearing");
                    toast.error("Error cleaning up data. Please try again.");
                }
            } catch (error) {
                console.error("Error during account cleanup:", error);
                toast.error("Error during account cleanup. Please try again.");
            }

            toast.success(response.data.message);
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error deleting profile");
            console.error("Error deleting profile:", error);
        } finally {
            setLoading(false);
            handleCloseModal();
        }
    };


    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '70%',
            padding: '16px',
        }}> 
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Permanently delete your account and all associated content. Any organizations without
                members will be immediately removed upon account deletion. Both the account and
                organizations will be irrecoverable. Please proceed with caution.
            </Typography>

            <Box>
                <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                    To confirm, please enter your account Email: {userData.Email}
                </Typography>
                <TextField
                    fullWidth
                    placeholder={userData.Email}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="outlined"
                    sx={textFieldSx}
                />
            </Box>

            <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleOpenModal}
                disabled={email !== userData.Email}
                sx={{
                    bgcolor: "#010320",
                    color: "#FFF",
                    mt: 2,
                    textTransform: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    '&:hover': {
                        bgcolor: "#010320",
                    },
                    '&.Mui-disabled': {
                        bgcolor: "#e0e0e0",
                        color: "#9e9e9e",
                    },
                }}
            >
                Delete Account
            </Button>

            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                aria-labelledby="delete-account-modal"
                aria-describedby="delete-account-confirmation"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 480,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    borderRadius: '16px',
                    p: 4,
                    outline: 'none',
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                    }}>
                        <Typography id="delete-account-modal" variant="h6" component="h6" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
                            Delete Account
                        </Typography>
                        <IconButton onClick={handleCloseModal} size="small" sx={{ p: 0 }}>
                            <CloseIcon size={24} />
                        </IconButton>
                    </Box>
                    <Box>
                        <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                            Are you sure you want to delete your account?
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            This action cannot be undone. All your data will be permanently deleted.
                        </Typography>
                    </Box>
                    <Box sx={{display:'flex', flexDirection:'row', gap:'16px'}}>
                    <Button
                        onClick={handleCloseModal}
                        fullWidth
                        sx={{
                            color: "#010320",
                            mt: 3,
                            textTransform: 'none',
                            alignContent:'center',
                            border:'1px solid #E5E7EB',
                            borderRadius:'12px',
                            '&:hover': {
                                bgcolor: 'transparent',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteAccount}
                        disabled={loading}
                        fullWidth
                        sx={{
                            bgcolor: "#010320",
                            color: "#FFF",
                            mt: 3,
                            alignContent:'center',
                            textTransform: 'none',
                            borderRadius: '8px',
                            padding: '10px 16px',
                            '&:hover': {
                                bgcolor: "#010320",
                            },
                            '&.Mui-disabled': {
                                bgcolor: "#e0e0e0",
                                color: "#9e9e9e",
                            },
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} sx={{ color: "#FFF" }} />
                        ) : (
                            "Delete Account"
                        )}
                    </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default AccountDeletion;